import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import './LoginScreen.css';

const LoginScreen = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const from = location.state?.from?.pathname || '/';

  // Check for verification success
  useEffect(() => {
    if (searchParams.get('verified') === 'true') {
      setSuccessMessage('Your email has been verified! You can now sign in.');
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(username, password);
      
      if (result.requirePasswordChange) {
        navigate('/change-password', { 
          state: { 
            userId: result.userId, 
            fromLogin: true,
            username: username
          } 
        });
      } else if (result.success) {
        // Redirect to default screen if available, otherwise to intended path or home
        const redirectPath = result.defaultScreenPath || from;
        navigate(redirectPath, { replace: true });
      }
    } catch (err) {
      if (err.requiresVerification) {
        setVerificationEmail(err.email);
        setError(err.message);
      } else {
        setVerificationEmail(null);
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!verificationEmail) return;
    
    setResendLoading(true);
    setResendSuccess(false);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3000/api'}/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: verificationEmail })
      });
      
      if (response.ok) {
        setResendSuccess(true);
        setError('');
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to resend verification email');
      }
    } catch (err) {
      setError('Failed to resend verification email');
    } finally {
      setResendLoading(false);
    }
  };

  const handleForgotPassword = () => {
    navigate('/forgot-password', { state: { username } });
  };

  return (
    <div className="login-screen">
      <div className="login-container">
        <div className="login-header">
          <div style={{ position: 'relative', display: 'inline-block', marginBottom: '10px' }}>
            <img
              src="/AmpNebulaSmall2.jpg"
              alt="AmpNebula"
              style={{ width: '200px', height: 'auto', borderRadius: '8px' }}
            />
            <span style={{
              position: 'absolute',
              top: '27%',
              right: '1%',
              fontSize: '0.5rem',
              color: 'rgba(255,255,255,0.8)',
              fontWeight: '600',
              textShadow: '0 1px 2px rgba(0,0,0,0.5)',
              lineHeight: 1
            }}>™</span>
          </div>
          <p>Sign in to your account</p>
        </div>
        
        <form onSubmit={handleSubmit} className="login-form">
          {successMessage && (
            <div className="success-message">
              {successMessage}
            </div>
          )}
          
          {error && (
            <div className="error-message">
              {error}
              {verificationEmail && (
                <div className="resend-verification">
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={resendLoading}
                    className="resend-button"
                  >
                    {resendLoading ? 'Sending...' : 'Resend verification email'}
                  </button>
                </div>
              )}
            </div>
          )}
          
          {resendSuccess && (
            <div className="success-message">
              Verification email sent! Please check your inbox.
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              disabled={loading}
              autoFocus
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
            />
          </div>
          
          <button 
            type="submit" 
            className="login-button"
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
          
          <button 
            type="button" 
            className="forgot-password-link"
            onClick={handleForgotPassword}
            disabled={loading}
          >
            Forgot your password?
          </button>
          
          <div className="signup-link">
            Don't have an account? <Link to="/signup">Sign up</Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginScreen;
